import {
  Dialog,
  DialogTitle,
  DialogHeader,
  DialogTrigger,
  DialogContent,
  DialogDescription,
} from '@design-system/Dialog';
import { useState } from 'react';
import { cn } from '@/utils/styles';
import { InfoIcon } from 'lucide-react';
import { Skeleton } from '@design-system/Skeleton';
import { ScrollArea } from '@design-system/ScrollArea';
import { Button, type ButtonProps } from '@design-system/Button';
import { NutritionPageServingsCount } from './NutritionPageServingsCount';
import { useGetDailyServingDataQuery } from '@viome/viome-base/src/usecase/Nutrition/NutritionQuery';
import type { IDailyServingDataResponse } from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';

export type NutritionPageServingGuideProps = ButtonProps;

const renderServingGuides = (
  dailyServingDataResponse?: IDailyServingDataResponse,
) => {
  if (!dailyServingDataResponse?.length) {
    return Array.from({ length: 5 }).map((_, index) => (
      <li key={`serving-skeleton-${index}`}>
        <Skeleton className="w-full h-14" />
      </li>
    ));
  }

  const renderedServingGuides = dailyServingDataResponse.map((servingData) => (
    <li
      key={servingData.macros}
      className="flex flex-row items-start gap-4 border-b last:border-none border-b-stroke-secondary-subtle pb-4"
    >
      <div className="flex flex-col">
        <span className="typography-caption1 !font-medium text-primary">
          {servingData.macros}
        </span>

        <span className="typography-caption1 text-secondary">
          {servingData.servings} servings
        </span>
      </div>

      <NutritionPageServingsCount
        className="ml-auto py-1 max-w-32"
        servingData={servingData}
      />
    </li>
  ));

  return renderedServingGuides;
};

export const NutritionPageServingGuide = ({
  className,
}: NutritionPageServingGuideProps) => {
  const [open, setOpen] = useState(false);

  const { data: dailyServingDataResponse } = useGetDailyServingDataQuery({
    enabled: open,
  });

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button variant="tertiary" className={cn('[&_svg:!size-4]', className)}>
          My Daily Servings Guide <InfoIcon size={16} aria-hidden />
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader className="gap-8 text-start">
          <DialogTitle className="typography-h1 text-primary">
            My Daily Servings Guide
          </DialogTitle>

          <DialogDescription className="typography-body text-primary">
            Daily servings are based on your age, height, weight, activity
            level, health goals, and health conditions, helping you create a
            balanced diet. Eating a variety of foods promotes a healthy
            microbiome.
          </DialogDescription>

          <ScrollArea className="max-h-96">
            <ul className="flex flex-col gap-4">
              {renderServingGuides(dailyServingDataResponse)}
            </ul>
          </ScrollArea>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
